import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import tokenManager from '../utils/tokenManager';
import { clearAuthData } from '../utils/auth';
import logger from '../utils/logger';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle the OAuth callback
    const handleAuth = async () => {
      try {
        setLoading(true);
        
        // First check if this is an OAuth callback
        const { data: { session: newSession }, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        if (newSession) {
          // Set the token before setting the user to ensure it's available for API calls
          tokenManager.setToken(newSession.access_token);
          setUser(newSession.user);
          logger.debug('AuthContext: User authenticated', { userId: newSession.user.id });
        } else {
          tokenManager.removeToken();
          clearAuthData();
          setUser(null);
          logger.debug('AuthContext: No session found');
        }
      } catch (error) {
        console.error('Error handling auth state:', error);
        tokenManager.removeToken();
        clearAuthData();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Set up the auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session) {
          // Make sure to update the token before setting the user
          tokenManager.setToken(session.access_token);
          setUser(session.user);
          logger.debug('AuthContext: Auth state changed - user authenticated', { userId: session.user.id });
        } else {
          tokenManager.removeToken();
          clearAuthData();
          setUser(null);
          logger.debug('AuthContext: Auth state changed - user signed out');
        }
      }
    );

    // Initial auth check
    handleAuth();

    return () => {
        subscription?.unsubscribe();
    };
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    signUp: (email, password, userData) => supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    }),
    signIn: async (email, password) => {
      // Clear any stale tokens before login
      tokenManager.removeToken();
      return supabase.auth.signInWithPassword({ email, password });
    },
    changePassword: (newPassword) => supabase.auth.updateUser({ password: newPassword }),
    signInWithGoogle: (redirectUrl) => {
      // Default to callback page if no redirect specified
      const redirectTo = redirectUrl ? 
        `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirectUrl)}` :
        `${window.location.origin}/auth/callback`;
        
      return supabase.auth.signInWithOAuth({ 
        provider: 'google',
        options: {
          redirectTo
        }
      });
    },
    signOut: async () => {
      try {
        await supabase.auth.signOut();
        tokenManager.removeToken();
        
        // Clear all localStorage auth data to prevent stale sessions
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('sb-bqipvtwfipmucdvqteih-auth-token');
        
        setUser(null);
      } catch (error) {
        console.error('Error signing out:', error);
        throw error;
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};